<template>
  <div class="attachments-tab-main scrollbar" v-loader.opacity.tab="loading">
    <div v-if="allExamOthersData.length > 0">
      <div class="section-gallery">
        <lightgallery-vue ref="lGallery" id="lightgallery" :settings="lightGallerySettingsOthers">
          <template v-for="(item, index) in examOthersMediaData" :key="index">
            <a v-if="isSupportedVideo(item)" class="lg-item-others" :id="`lg-item-others-${item.id}`"
              data-lg-size="1280-720" :data-video="getOtherVideoSource(item)"
              :data-sub-html="`<h4>${item.originalName}</h4>`">
            </a>

            <a v-else-if="isSupportedImage(item)" class="lg-item-others" :id="`lg-item-others-${item.id}`"
              :data-src="getOtherMediaSource(item)" :data-sub-html="`<h4>${item.originalName}</h4>`">
            </a>
          </template>
        </lightgallery-vue>
      </div>

      <BaseTable class="no-border attachments-table" :tabledata="othersTableData" @item-opened="handlePreview">
        <template v-slot:column_icon="{ item }">
          <div class="file-icon-cell">
            <i :class="$utils.getFileIcon(item.type)"></i>
          </div>
        </template>

        <template v-slot:column_actions="{ item }">
          <div class="action-group">
            <button class="btn btn-outline-secondary" type="button" name="button" :data-title="$t('view')"
              data-test="`btnView`" @click.stop="handlePreview(item)">
              <IconEye />
            </button>

            <button class="btn btn-outline-secondary" type="button" name="button" :data-title="$t('tooltips.download')"
              data-test="`btnDownload`" @click.stop="doDownload(item)">
              <IconDownload />
            </button>
          </div>
        </template>
      </BaseTable>
    </div>

    <div class="no-results-box" v-else-if="!loading && !allExamOthersData.length">
      <BaseNoResults :title="$t('externalPatient.emptyAttachmentsTitle')"
        :text="$t('externalPatient.emptyAttachmentsText')" />
    </div>

    <teleport to="body">
      <BaseFilePreviewModal ref="baseFilePreviewModal" />
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.attachments-tab-main {
  width:                          100%;
  min-height:                     440px;
  max-height:                     440px;
  overflow-y:                     auto;
  overflow-x:                     hidden;
  margin-bottom:                  26px;
}

:deep(.loading-overlay) {
  min-height:                     auto !important;
}

.section-gallery {
  display:                        none;
}

.file-icon-cell i {
  color:                          var(--Brand-Secondary, #3CB391);
  font-size:                      24px;
}

.action-group {
  display:                        flex;
  justify-content:                flex-end;
  gap:                            10px;

  .btn {
    background:                   var(--Shades-BG);

    &:hover,
    &:focus,
    &:active,
    &.active {
      color:                      var(--Brand-Secondary, #3CB391);
      background:                 var(--Shades-10);
    }
  }
}

.no-results-box {
  width:                          100%;
  display:                        flex;
  flex-direction:                 column;
  align-items:                    center;
  text-align:                     center;
}

.attachments-table {
  width:                          100% !important;
}

:deep(.attachments-table thead) {
  display:                        none !important;
}

:deep(.vtable-mob),
:deep(.table-responsive.desk) {
  overflow:                       visible !important;
  height:                         auto !important;
  max-height:                     none !important;
  flex:                           none !important; 
}

:deep(.vtable-mob ul) {
  margin-bottom:                  0 !important;
}

@media (min-width: 768px) {
  .attachments-tab-main {
    min-height:                   370px;
    max-height:                   370px;
  }
}

@media (max-width: 991px) {
  .attachments-tab-main {
    max-height:                   unset !important;
    height:                       auto !important;
    overflow-y:                   visible !important;
    padding-right:                0;
  }

  :deep(.vtable-mob) {
    .inside-box-mob-item {
      display:                    flex !important;
      flex-wrap:                  wrap !important;
      align-items:                center !important;

      p:nth-child(1) {
        order:                    1 !important;
        flex:                     0 0 auto !important;
      }

      p:nth-child(4) {
        order:                    2 !important;
        margin-left:              auto !important;
        width:                    auto !important;
        .action-group {
          justify-content:        flex-end !important;
        }
      }

      p:nth-child(2) {
        order:                    3 !important;
        flex:                     1 1 100% !important;
      }

      p:nth-child(3) {
        order:                    4 !important;
        flex:                     1 1 100% !important;
      }
    }
  }
}
</style>

<script>
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from '@/assets/dependencies/lightGallery/plugins/zoom';

export default {
  name: 'ExternalExamAttachmentsTab',
  data: () => ({
    loading: false,
    allExamOthersData: [],
    lightGallerySettingsOthers: {
      selector: '.lg-item-others',
      download: true,
      counter: true,
      videojs: false,
      plugins: [lgVideo, lgZoom, lgFullscreen]
    },
    examID: null,
  }),
  computed: {
    examOthersMediaData() {
      return this.allExamOthersData.filter(item => this.$utils.isSupportedGalleryImage(item?.extension) || this.$utils.isSupportedGalleryVideo(item?.extension));
    },
    othersTableData() {
      return {
        items: this.allExamOthersData,
        columns: [
          { key: 'icon', name: '', type: 'custom', width: '2%' },
          { name: '', type: 'string', value: 'originalName', width: '39%' },
          { name: '', type: 'string', value: 'description', width: '39%' },
          { key: 'actions', name: '', type: 'custom', width: '20%' }
        ]
      };
    },
  },
  created() {
    this.examID = this.$route.params.examID
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      NProgress.start();
      try {
        let response = await this.$externalService.getExam({id: this.examID});
        let examDataList = response.examDataList || [];
        this.allExamOthersData = examDataList.filter(file => file.type.startsWith('OTHER_'));
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    async doDownload(item) {
      this.loading = true;
      NProgress.start();
      try {
        let params = {};
        if (item.id) 
          params['id'] = item.id;

        if (item.uuid) 
          params['uuid'] = item.uuid;

        const url = this.$externalService.getDownloadFileUrl(params);
        this.$utils.triggerFileDownload(url);
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    async handlePreview(item) {
      const targetItem = item.item ? item.item : item;
      const isImage = this.$utils.isSupportedGalleryImage(targetItem?.extension);
      const isVideo = this.$utils.isSupportedGalleryVideo(targetItem?.extension);

      if (isImage || isVideo) {
        const anchor = document.getElementById(`lg-item-others-${targetItem.id}`);
        if (anchor) {
          anchor.click();
        }
      } else {
        const urlFile = this.$externalService.getDownloadFileUrl({ uuid: targetItem.uuid, inline: true });

        const isPreviewOpened = await this.$refs.baseFilePreviewModal.show({
          url: urlFile,
          file: targetItem
        });

        if (isPreviewOpened === 'unsupported') {
          this.$message.popup(this, 'exam.previewUnavailableDownloadStarted', 'operationWarning', 'warn');
          await this.doDownload(targetItem);
        }
      }
    },
    getOtherMediaSource(item) {
      return this.$externalService.getDownloadFileUrl({ uuid: item.uuid });
    },
    getOtherVideoSource(item) {
      const url = this.getOtherMediaSource(item);
      let result = {
        "source": [{ "src": url, "type": "video/mp4" }],
        "attributes": { "preload": false, "playsinline": false, "controls": true, "loop": true }
      }
      return JSON.stringify(result);
    },
    isSupportedVideo(item) {
      return this.$utils.isSupportedGalleryVideo(item?.extension);
    },
    isSupportedImage(item) {
      return this.$utils.isSupportedGalleryImage(item?.extension);
    }
  }
}
</script>
